import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { LetDirective } from '@ngrx/component';
import { isEmpty, isNil } from 'lodash';
import { DropdownModule } from 'primeng/dropdown';

import { CallerIdStrategyType } from '@pwp-common';

import { provideValidator } from '../../../../common/form/provide-validator';
import { provideValueAccessor } from '../../../../common/form/provide-value-accessor';
import { MenuOption } from '../../../../common/form/typings/menu-options';
import { FormFieldHelpComponent } from '../../../form/form-field-help/form-field-help.component';
import { FormGroupControlValueAccessor } from '../../../generic/abstract-classes/form-group-control-value-accessor';
import { ObjAutocompleteV2Module } from '../../../generic/obj-autocomplete-v2/obj-autocomplete-v2.module';
import { CallerIdStrategyEditorOutput } from '../editor-output/caller-id-strategy-editor-output';

import { callerIdStrategyValidator } from './validators/caller-id-strategy-validator';

@UntilDestroy()
@Component({
  selector: 'app-caller-id-strategy-editor',
  standalone: true,
  imports: [
    CommonModule,
    DropdownModule,
    FormFieldHelpComponent,
    FormsModule,
    LetDirective,
    ObjAutocompleteV2Module,
    ReactiveFormsModule,
    TranslocoModule,
  ],
  templateUrl: './caller-id-strategy-editor.component.html',
  styleUrl: './caller-id-strategy-editor.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideValueAccessor(() => CallerIdStrategyEditorComponent),
    provideValidator(() => CallerIdStrategyEditorComponent),
  ],
})
export class CallerIdStrategyEditorComponent
  extends FormGroupControlValueAccessor<CallerIdStrategyEditorOutput, CallerIdStrategyEditorOutput>
  implements OnInit
{
  /////////////////////////////////////////////////////////////////////////////////////////////
  // Inputs
  /////////////////////////////////////////////////////////////////////////////////////////////

  @Input() public excludedStrategyTypes?: CallerIdStrategyType[] | null;

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////////////////////

  public readonly form = new FormGroup(
    {
      callerIdObjId: new FormControl(''),
      type: new FormControl(''),
    },
    { validators: callerIdStrategyValidator },
  );

  public possibleValuesCallerIdStrategy: MenuOption<CallerIdStrategyType>[] = [];

  private readonly allPossibleValuesCallerIdStrategy: MenuOption<CallerIdStrategyType>[] = [
    {
      label: 'callerIdStrategyLabelExplicit',
      description: 'callerIdStrategyDescriptionExplicit',
      value: CallerIdStrategyType.explicit,
    },
    {
      label: 'callerIdStrategyLabelForward',
      description: 'callerIdStrategyDescriptionForward',
      value: CallerIdStrategyType.forward,
    },
    {
      label: 'callerIdStrategyLabelReceivedAt',
      description: 'callerIdStrategyDescriptionReceivedAt',
      value: CallerIdStrategyType.receivedAt,
    },
  ];

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  /////////////////////////////////////////////////////////////////////////////////////////////

  public ngOnInit(): void {
    super.ngOnInit();

    this.possibleValuesCallerIdStrategy = this.allPossibleValuesCallerIdStrategy.filter(
      (option) => !this.excludedStrategyTypes?.includes(option.value!),
    );
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Write Value
  /////////////////////////////////////////////////////////////////////////////////////////////

  public writeValue(value: CallerIdStrategyEditorOutput | undefined) {
    if (isNil(value)) {
      return;
    }

    this.form.patchValue(value, { emitEvent: true });
    this.changeDetectorRef.detectChanges();
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Parse Value Change
  /////////////////////////////////////////////////////////////////////////////////////////////

  public parseValueChange(value: CallerIdStrategyEditorOutput): CallerIdStrategyEditorOutput {
    const { callerIdObjId, type } = value;

    if (isEmpty(callerIdObjId) && isEmpty(type)) {
      return null;
    }

    return value;
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Validation Errors
  /////////////////////////////////////////////////////////////////////////////////////////////

  public makeValidationErrors(): ValidationErrors {
    return {
      'caller-id-strategy-editor': this.form.value,
    };
  }
}
